import { ChangeEvent } from 'react';
import { Icon, UploadIcon, useToast } from '@/src/components/ui';
import { Input } from '@workspace/ui/components/input';
import { Button } from '@workspace/ui/components/button';
import { cn } from '@workspace/ui/lib/utils';
import { POST_MEDIA_LIMITS } from '@/src/constants/constants';

export type VideoValue = {
  file: File | Blob;
  url: string;
};

type Props = {
  value?: VideoValue;
  onChange: (value?: VideoValue) => void;
  onError?: (message: string) => void;
  placeholder?: string;
  className?: string;
};

const MAX_VIDEO_MB = 50;

const isQuickTimeMov = (file: File) =>
  file.type === 'video/quicktime' || file.name.toLowerCase().endsWith('.mov');

export const VideoUpload = ({
  value,
  onChange,
  onError,
  placeholder = 'Upload video',
  className = '',
}: Props) => {
  const uploadId = `video-upload-${Math.random().toString(36).slice(2)}`;
  const toast = useToast();

  const clearNativeInput = () => {
    const input = document.getElementById(uploadId) as HTMLInputElement | null;
    if (input) input.value = '';
  };

  const fail = (message: string) => {
    toast.error(message);
    onError?.(message);
    onChange(undefined);
    clearNativeInput();
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isVideo = file.type.startsWith('video/');
    const isGif = file.type === 'image/gif';

    if (!isVideo && !isGif) {
      fail('Please select a video or GIF file');
      return;
    }

    if (isQuickTimeMov(file)) {
      fail(
        'MOV (QuickTime) videos are not supported. Please upload MP4 or GIF.'
      );
      return;
    }

    if (file.size > POST_MEDIA_LIMITS.MAX_VIDEO_SIZE_BYTES) {
      fail(`File must be less than ${MAX_VIDEO_MB}MB`);
      return;
    }

    onError?.('');
    onChange({
      file,
      url: URL.createObjectURL(file),
    });
  };

  const handleRemove = () => {
    onChange(undefined);
    onError?.('');
    clearNativeInput();
  };

  const isGif = value?.file?.type === 'image/gif';

  return (
    <div className={cn('ml-0 w-[275px]', className)}>
      {value?.file ? (
        <div className="relative group w-full aspect-video overflow-hidden rounded-lg">
          {isGif ? (
            <img
              src={value.url}
              className="w-full h-full object-cover"
              alt="Uploaded GIF"
            />
          ) : (
            <video
              src={value.url}
              className="w-full h-full object-cover"
              controls
            />
          )}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute top-2 right-2 bg-white/80 hover:bg-white group-hover:flex hidden"
            onClick={handleRemove}
          >
            <Icon name="delete" className="size-5 text-red-500" />
          </Button>
        </div>
      ) : (
        <div
          className="p-2 w-[275px] aspect-video flex flex-col items-center justify-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 hover:border-blue-primary cursor-pointer transition-all"
          onClick={() => document.getElementById(uploadId)?.click()}
        >
          <UploadIcon />
          <Input
            id={uploadId}
            type="file"
            className="hidden"
            accept="video/mp4,image/gif"
            onChange={handleFileChange}
          />
          <p className="text-sm font-medium text-gray-600">{placeholder}</p>
          <p className="text-xs text-gray-400">Max {MAX_VIDEO_MB}MB</p>
        </div>
      )}
    </div>
  );
};

